import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { MonthFilterField, fromMonthValue, toMonthValue } from '../../../shared/components/MonthFilterField';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { useAuth } from '../../../shared/auth/useAuth';
import { tiposHoraHooks } from '../../cadastros-gerais/tipos-hora/hooks';
import { timesheetEntriesHooks, useTimesheetEntriesList, useTimesheetEntryTotals } from '../hooks';
import { tempoEfetivoDecimal } from '../utils';
import { downloadApontamentosRelatorio, timesheetEntriesApi } from '../api';
import { TimesheetEntryFormDialog } from '../TimesheetEntryFormDialog';
import { ResumoHorasDialog } from '../components/ResumoHorasDialog';
import type {
  TimesheetEntryDto,
  TimesheetEntryListItem,
  TimesheetEntryTotalsFilters,
  TimesheetEntryTotalsResponse,
} from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'data',
  sortOrder: 'desc',
};

interface PageFilters {
  mes: number;
  ano: number;
  colaboradorCodigo: number | null;
  clienteCodigo: number | null;
}

function defaultFilters(lockedColabId: number | null = null): PageFilters {
  const hoje = dayjs();
  return {
    mes: hoje.month() + 1,
    ano: hoje.year(),
    colaboradorCodigo: lockedColabId,
    clienteCodigo: null,
  };
}

/** Mês fechado: do dia 1 ao último dia. */
function periodoFechado(mes: number, ano: number): { dataInicial: string; dataFinal: string } {
  const inicio = dayjs(new Date(ano, mes - 1, 1));
  return {
    dataInicial: inicio.format('YYYY-MM-DD'),
    dataFinal: inicio.endOf('month').format('YYYY-MM-DD'),
  };
}

/** `HH:MM` → decimal no padrão da folha (8:00 = 8,00; 2:40 = 2,67). */
function horasParaDecimal(horas: string | null | undefined): string {
  if (!horas) return '—';
  const match = /^(\d+):(\d{2})$/.exec(horas.trim());
  if (!match) return horas;
  const decimal = Number(match[1]) + Number(match[2]) / 60;
  return decimal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatarTempoEfetivo(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function TotalsHeader({
  totals,
  loading,
}: {
  totals: TimesheetEntryTotalsResponse | undefined;
  loading: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Box>
          <Typography variant="overline" color="text.secondary">
            {t('features.apontamentos.totals.total')}
          </Typography>
          <Typography variant="h4" component="div" sx={{ fontFamily: 'monospace' }}>
            {loading ? '—' : (totals?.totalHoras ?? '00:00')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(totals?.porTipoHora ?? []).map((b) => (
            <Chip
              key={b.tipoHoraCodigo}
              label={`${b.tipoHoraNome} ${b.horas}`}
              size="small"
              variant="outlined"
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
}

export function TimesheetEntriesListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuth();
  const isColab = user?.perfil === 'COLABORADOR';
  const lockedColabId = isColab ? (user?.externalId ?? null) : null;
  const {
    query,
    setQuery,
    filters,
    setFilters,
    clear,
  } = usePersistedGridState('apontamentos', DEFAULT_QUERY, defaultFilters(lockedColabId));
  useEffect(() => {
    if (lockedColabId != null && filters.colaboradorCodigo !== lockedColabId) {
      setFilters((current) => ({ ...current, colaboradorCodigo: lockedColabId }));
    }
  }, [lockedColabId, filters.colaboradorCodigo, setFilters]);
  const [editing, setEditing] = useState<TimesheetEntryDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<TimesheetEntryListItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const [resumoOpen, setResumoOpen] = useState(false);

  const deleteMutation = timesheetEntriesHooks.useDelete();
  const tiposHoraQuery = tiposHoraHooks.useList({ page: 1, pageSize: 100, includeInactive: true });
  const multiplicadorPorTipo = useMemo(() => {
    const map = new Map<number, number>();
    for (const tipo of tiposHoraQuery.data?.data ?? []) {
      map.set(tipo.id, tipo.multiplicadorHora);
    }
    return map;
  }, [tiposHoraQuery.data]);

  const mes = filters.mes >= 1 && filters.mes <= 12 ? filters.mes : dayjs().month() + 1;
  const ano = filters.ano >= 2000 ? filters.ano : dayjs().year();
  const sharedFilters: TimesheetEntryTotalsFilters = useMemo(
    () => ({
      ...periodoFechado(mes, ano),
      colaboradorCodigo: filters.colaboradorCodigo,
      clienteCodigo: filters.clienteCodigo,
    }),
    [mes, ano, filters.colaboradorCodigo, filters.clienteCodigo],
  );

  function setPeriodo(partial: Partial<Pick<PageFilters, 'mes' | 'ano'>>): void {
    setFilters((current) => ({ ...current, ...partial }));
    setQuery((current) => ({ ...current, page: 1 }));
  }
  const listQuery = useTimesheetEntriesList(query, sharedFilters);
  const totals = useTimesheetEntryTotals(sharedFilters);

  async function openEdit(row: TimesheetEntryListItem): Promise<void> {
    try {
      setEditing(await timesheetEntriesApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function exportarExcel(): Promise<void> {
    setExporting(true);
    try {
      const blob = await downloadApontamentosRelatorio(sharedFilters);
      const filename = `apontamentos-${ano}-${String(mes).padStart(2, '0')}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setExporting(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await deleteMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.apontamentos.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<TimesheetEntryListItem>[] = [
    {
      field: 'data',
      headerName: t('features.apontamentos.columns.data'),
      width: 110,
      valueFormatter: (value: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '—'),
    },
    {
      field: 'horario',
      headerName: t('features.apontamentos.columns.horario'),
      width: 130,
      sortable: false,
      valueFormatter: (value: string | null | undefined) => value || '—',
    },
    ...(isColab
      ? []
      : [
          {
            field: 'colaboradorNome',
            headerName: t('features.apontamentos.columns.colaborador'),
            flex: 1,
            minWidth: 140,
            sortable: false,
          } satisfies GridColDef<TimesheetEntryListItem>,
        ]),
    {
      field: 'clienteNome',
      headerName: t('features.apontamentos.columns.cliente'),
      flex: 1.2,
      minWidth: 160,
      sortable: false,
    },
    {
      field: 'clienteProjetoNome',
      headerName: t('features.apontamentos.columns.projeto'),
      flex: 1,
      minWidth: 140,
      sortable: false,
    },
    {
      field: 'codigoExterno',
      headerName: t('features.apontamentos.columns.codigoExterno'),
      flex: 1,
      minWidth: 140,
      sortable: false,
      valueFormatter: (value: string | null | undefined) => value || '—',
    },
    {
      field: 'horas',
      headerName: t('features.apontamentos.columns.horas'),
      width: 90,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {(params.value as string) || '—'}
        </Box>
      ),
    },
    {
      field: 'tempoApontado',
      headerName: t('features.apontamentos.columns.tempoApontado'),
      width: 140,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueGetter: (_value, row) => horasParaDecimal(row.horas),
    },
    ...(isColab
      ? []
      : [
          {
            field: 'tempoEfetivo',
            headerName: t('features.apontamentos.columns.tempoEfetivo'),
            width: 130,
            align: 'right',
            headerAlign: 'right',
            sortable: false,
            valueGetter: (_value, row) => {
              if (row.tempoEfetivo != null && row.tempoEfetivo !== '') return row.tempoEfetivo;
              const multiplicador =
                row.tipoHoraCodigo != null ? multiplicadorPorTipo.get(row.tipoHoraCodigo) : undefined;
              return tempoEfetivoDecimal(row.horas, multiplicador);
            },
            valueFormatter: (value: number | string | null | undefined) => formatarTempoEfetivo(value),
          } satisfies GridColDef<TimesheetEntryListItem>,
        ]),
    {
      field: 'actions',
      headerName: '',
      width: 88,
      sortable: false,
      filterable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const vinculadoNota =
          params.row.notaFiscalColaboradorCodigo != null &&
          params.row.notaFiscalColaboradorCodigo !== 0;
        if (vinculadoNota) return null;
        return (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.apontamentos.actions.edit', { id: params.row.id })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('features.apontamentos.actions.deleteTooltip')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.apontamentos.actions.delete', { id: params.row.id })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        );
      },
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.apontamentos')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.apontamentos')}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                filters.colaboradorCodigo == null
                  ? t('features.apontamentos.resumo.selecioneColaborador')
                  : ''
              }
            >
              <span>
                <Button
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={() => setResumoOpen(true)}
                  disabled={filters.colaboradorCodigo == null}
                >
                  {t('features.apontamentos.actions.resumo')}
                </Button>
              </span>
            </Tooltip>
            {!isColab && (
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => void exportarExcel()}
                disabled={exporting}
              >
                {t('features.apontamentos.actions.exportarExcel')}
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
              {t('features.apontamentos.actions.new')}
            </Button>
          </Stack>
        }
      />

      <TotalsHeader totals={totals.data} loading={totals.isLoading} />

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <MonthFilterField
            label={t('components.monthFilter.competencia')}
            value={toMonthValue(ano, mes)}
            onChange={(value) => {
              const parsed = fromMonthValue(value);
              if (parsed.year == null || parsed.month == null) return;
              setPeriodo({ mes: parsed.month, ano: parsed.year });
            }}
          />
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <ColaboradorSelect
            value={filters.colaboradorCodigo}
            onChange={(id) => setFilters((f) => ({ ...f, colaboradorCodigo: id }))}
            label={t('features.apontamentos.filters.colaborador')}
            disabled={isColab}
          />
        </Box>
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <ClienteSelect
            value={filters.clienteCodigo}
            onChange={(id) => setFilters((f) => ({ ...f, clienteCodigo: id }))}
            label={t('features.apontamentos.filters.cliente')}
          />
        </Box>
        </Stack>
      </Paper>

      <DataTable<TimesheetEntryListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.apontamentos.searchPlaceholder')}
      />

      <ResumoHorasDialog
        open={resumoOpen}
        onClose={() => setResumoOpen(false)}
        colaboradorCodigo={filters.colaboradorCodigo}
        initialDate={sharedFilters.dataInicial}
      />

      <TimesheetEntryFormDialog
        open={editing !== null}
        entry={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.apontamentos.deleteConfirm.title')}
        description={t('features.apontamentos.deleteConfirm.description')}
        confirmLabel={t('features.apontamentos.actions.confirmDelete')}
        loading={deleteMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
